import { Badge } from '@@/Badge';

import { DriftMode } from '../workflows/wizard/types';
import { WorkflowArtifact } from '../workflows/types';

/**
 * How each drift mode reads outside the form.
 *
 * Shorter than the labels the wizard offers, which explain the choice being
 * made. Here the choice has been made, and a row of badges has to stay a row.
 */
const DRIFT_LABELS: Record<DriftMode, string> = {
  disabled: 'Drift detection off',
  warn: 'Report drift',
  enabled: 'Reconcile drift',
};

/**
 * The policy a reconciled artifact is kept under, as badges.
 *
 * Shown wherever a workflow is read rather than edited, because these three
 * settings decide what the engine does to a cluster - whether it puts drift
 * back, whether it deletes what the source dropped, and whether it recreates a
 * resource it cannot update - and looking that up meant opening the edit form.
 *
 * Nothing is rendered for an artifact that is not reconciled: an Edge stack has
 * no reconcile policy, and empty badges beside it would suggest it does.
 */
export function ReconcileBadges({ artifact }: { artifact: WorkflowArtifact }) {
  const reconcile = artifact.reconcile;

  if (!hasReconcilePolicy(artifact) || !reconcile) {
    return null;
  }

  const driftMode = (reconcile.driftMode ?? 'warn') as DriftMode;
  // Both default to what the engine does when the deployment does not say,
  // which is how the edit form reads them too.
  const prunes = reconcile.prune ?? true;
  const recreates = reconcile.forceApply ?? false;

  return (
    <div className="inline-flex flex-wrap items-center gap-1">
      <Badge type={driftMode === 'enabled' ? 'infoSecondary' : 'muted'}>
        {DRIFT_LABELS[driftMode]}
      </Badge>

      {/* Only the setting that acts is worth a badge. Saying "no pruning" and
          "no force recreate" on every artifact that left the defaults alone
          would be three badges saying nothing on most rows. */}
      {prunes && <Badge type="muted">Prune resources</Badge>}
      {recreates && <Badge type="warnSecondary">Force recreate</Badge>}
    </div>
  );
}

/**
 * Whether this artifact has a policy to show, for a caller deciding whether to
 * make room for one.
 *
 * Exported rather than left to the caller to infer: a row that reserves space
 * and then renders nothing is the same bug as a row that renders badges
 * nobody can see.
 */
export function hasReconcilePolicy(artifact: WorkflowArtifact): boolean {
  return artifact.type === 'gitopsDeployment' && !!artifact.reconcile;
}
